<!-- Dialog Header -->
<h2 mat-dialog-title>
  <div class="d-flex justify-content-between align-items-center">
    <span>{{ 'MODAL_ADD_PATIENT.TITLE' | translate }}</span>
    <button mat-icon-button (click)="dialogRef.close()" aria-label="Close">
      <mat-icon>close</mat-icon>
    </button>
  </div>
</h2>

<!-- Dialog Content with Stepper -->
<mat-dialog-content>

  <!-- Angular Material Stepper with custom indicators -->
  <mat-stepper linear #stepper displayDefaultIndicatorType="false">
    <!-- Step 1: Patient Form -->
    <mat-step [stepControl]="form">
      <ng-template matStepLabel>
        <span class="step-label-wrap" *ngIf="stepper.selectedIndex === 0">{{ 'MODAL_ADD_PATIENT.FORM.PATIENT_DETAILS' | translate }}</span>
      </ng-template>
      <form id="patientForm" [formGroup]="form" class="patient-form">
        <div formGroupName="patientInfo">
          <div class="row">
      
            <!-- Name Field -->
            <div class="col-12 mb-3">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.NAME' | translate }}</mat-label>
                <input title="given_name" matInput type="text" formControlName="given_name" trimOnBlur
                       [placeholder]="'MODAL_ADD_PATIENT.FORM.NAME_PLACEHOLDER' | translate">
                @if(form.get('patientInfo.given_name')?.invalid && form.get('patientInfo.given_name')?.touched) {
                  <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.NAME.REQUIRED' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>

            <!-- Last Name Field -->
            <div class="col-12 mb-3">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.LAST_NAME' | translate }}</mat-label>
                <input title="family_name" matInput type="text" formControlName="family_name" trimOnBlur
                       [placeholder]="'MODAL_ADD_PATIENT.FORM.LAST_NAME_PLACEHOLDER' | translate">
                @if(form.get('patientInfo.family_name')?.invalid && form.get('patientInfo.family_name')?.touched) {
                  <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.LAST_NAME.REQUIRED' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>

            <!-- Gender Dropdown -->
            <div class="col-12 mb-3">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.GENDER' | translate }}</mat-label>
                <mat-select formControlName="gender">
                  @if(genders$ | async; as genders) {
                    @if(genders.length > 0) {
                      @for(gender of genders; track gender.id) {
                        <mat-option [value]="gender.id">{{ gender.name }}</mat-option>
                      }
                    } @else {
                      <mat-option value="void">
                        {{ 'MODAL_ADD_PATIENT.FORM.NOT_DATA' | translate }}
                      </mat-option>
                    }
                  }
                </mat-select>
                @if(form.get('patientInfo.gender')?.invalid && form.get('patientInfo.gender')?.touched) {
                  <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.GENDER.REQUIRED' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>
      
            <!-- Country of Birth Dropdown -->
            <div class="col-12 mb-3">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.COUNTRY_OF_BIRTH' | translate }}</mat-label>
                <mat-select formControlName="country_of_birth">
                  @if(countries$ | async; as countries) {
                    @if(countries.length > 0) {
                      @for(country of countries; track country.iso_code) {
                        <mat-option [value]="country.iso_code">{{ country.name }}</mat-option>
                      }
                    } @else {
                      <mat-option value="void">
                        {{ 'MODAL_ADD_PATIENT.FORM.NOT_DATA' | translate }}
                      </mat-option>
                    }
                  }
                </mat-select>
                @if(form.get('patientInfo.country_of_birth')?.invalid && form.get('patientInfo.country_of_birth')?.touched) {
                  <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.COUNTRY_OF_BIRTH.REQUIRED' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>
      
            <!-- Date of Birth Field -->
            <div class="col-12 mb-3">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.DATE_OF_BIRTH' | translate }}</mat-label>
                <input title="date_of_birth" matInput [matDatepicker]="picker" type="text" formControlName="date_of_birth"
                       [max]="maxDate" [min]="minDate" readonly>
                <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
                @if(form.get('patientInfo.date_of_birth')?.invalid && form.get('patientInfo.date_of_birth')?.touched) {
                  <mat-error>{{ form.get('patientInfo.date_of_birth')?.errors?.['invalidDate'] }}</mat-error>
                }
              </mat-form-field>
            </div>
      
            <!-- Relationship Dropdown -->
            <div class="col-12 mb-3">
              <mat-form-field appearance="outline" class="w-100">
                <mat-label>{{ 'MODAL_ADD_PATIENT.FORM.RELATIONSHIPS' | translate }}</mat-label>
                <mat-select formControlName="relationship">
                  @if(relationships$ | async; as relationships) {
                    @if(relationships.length > 0) {
                      @for(relationship of relationships; track relationship.id) {
                        <mat-option [value]="relationship.id">{{ relationship.name }}</mat-option>
                      }
                    } @else {
                      <mat-option value="void">
                        {{ 'MODAL_ADD_PATIENT.FORM.NOT_DATA' | translate }}
                      </mat-option>
                    }
                  }
                </mat-select>
                @if(form.get('patientInfo.relationship')?.invalid && form.get('patientInfo.relationship')?.touched) {
                  <mat-error>{{ 'PATIENT_DETAIL.VALIDATION.RELATIONSHIP.REQUIRED' | translate }}</mat-error>
                }
              </mat-form-field>
            </div>
      
          </div>
        </div>
      </form>
      
      <!-- Navigation for Step 1 -->
      <div class="d-flex justify-content-end mt-3">
        <button mat-raised-button color="primary" matStepperNext 
          [disabled]="!form.get('patientInfo')?.valid"
          (click)="nextStep()">
          <mat-icon>arrow_forward</mat-icon>
        </button>

      </div>
    </mat-step>

    <!-- Step 2: Consents -->
    <mat-step>
      <ng-template matStepLabel>
        <span class="step-label-wrap" *ngIf="stepper.selectedIndex === 1">Consent</span>
      </ng-template>
      <div class="pt-3">
        @if(consentHtml) {
          <div class="scrollable-content" [innerHTML]="consentHtml"></div>
        } @else {
          <p>{{ 'LOADING' | translate }}</p>
        }
      </div>
      <!-- Navigation for Step 2 -->
      <div class="d-flex justify-content-between mt-3">
        <button mat-raised-button color="primary" matStepperPrevious>
          <mat-icon>arrow_back</mat-icon>
        </button>
        <button mat-raised-button color="primary"
          (click)="onSubmit()"
          [disabled]="form.invalid">
          <mat-icon>check</mat-icon>
            {{ 'MODAL_ADD_PATIENT.ACTIONS.ACCEPT' | translate }}
        </button>
      </div>
    </mat-step>
  </mat-stepper>
</mat-dialog-content>